import React, { useState, useEffect } from 'react';
import * as api from './api';
import HeaderNav from './components/layout/HeaderNav';
import SidebarNav from './components/layout/SidebarNav';
import DrawerModal from './components/layout/DrawerModal';
import ModalContainer from './components/common/ModalContainer';
import DashboardView from './components/views/DashboardView';
import CaptainsView from './components/views/CaptainsView';
import ArtistsView from './components/views/ArtistsView';
import PropertiesView from './components/views/PropertiesView';
import FitnessView from './components/views/FitnessView';
import LokaMapView from './components/views/LokaMapView';
import UsersView from './components/views/UsersView';
import FinanceView from './components/views/FinanceView';
import SettingsView from './components/views/SettingsView';
import LoginView from './components/auth/LoginView';
import MusicPublisher from './components/MusicPublisher';
import ApprovalsView from './components/views/ApprovalsView';
import PartyManagerView from './components/views/PartyManagerView';
import BookingsManagerView from './components/views/BookingsManagerView';
import AdsManagerView from './components/views/AdsManagerView';
import PaymentsView from './components/views/PaymentsView';
import PwaInstallPrompt from './components/common/PwaInstallPrompt';
import { CheckCircle2 } from 'lucide-react';
import './App.css';

export default function App() {
  // Auth state
  const [isAuth, setIsAuth] = useState(api.isLoggedIn());
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);

  // Theme & Layout state
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [actionSuccessMsg, setActionSuccessMsg] = useState('');

  // Collections state
  const [stats, setStats] = useState(null);
  const [captains, setCaptains] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [artists, setArtists] = useState([]);
  const [publishedTracks, setPublishedTracks] = useState([]);

  // Slot matrix state
  const [selectedSlotRoom, setSelectedSlotRoom] = useState(null);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [selectedSlotDate, setSelectedSlotDate] = useState(new Date().toISOString().split('T')[0]);

  // Drawer & Modals state
  const [drawerItem, setDrawerItem] = useState(null);
  const [drawerType, setDrawerType] = useState(''); // 'captain', 'room', 'report', 'booking'
  const [rejectionReason, setRejectionReason] = useState('');

  const [unlockModalCaptain, setUnlockModalCaptain] = useState(null);
  const [unlockModules, setUnlockModules] = useState([]);
  const [unlockDuration, setUnlockDuration] = useState('30');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);

  // Form fields for Create Modal
  const [createModuleType, setCreateModuleType] = useState('building');
  const [createCategoryType, setCreateCategoryType] = useState('flat');
  const [createTitle, setCreateTitle] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [createPrice, setCreatePrice] = useState('');
  const [createPriceType, setCreatePriceType] = useState('month');
  const [createVenueAddress, setCreateVenueAddress] = useState('');
  const [createEventTime, setCreateEventTime] = useState('');
  const [createCity, setCreateCity] = useState('Indore');
  const [createImageUrl, setCreateImageUrl] = useState('');
  const [createCoupleAllowed, setCreateCoupleAllowed] = useState(true);

  // Initialize theme attribute
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Data fetching orchestrators
  const loadDashboardData = async () => {
    try {
      const data = await api.getDashboardStats();
      if (data.success) setStats(data.data);
    } catch (e) {
      console.warn('Dashboard stats load error:', e.message);
    }
  };

  const loadCaptains = async () => {
    try {
      const data = await api.getCaptains();
      if (data.success) {
        setCaptains(data.data?.captains || (Array.isArray(data.data) ? data.data : []));
      }
    } catch (e) {
      console.warn('Captains load error:', e.message);
    }
  };

  const loadRooms = async () => {
    try {
      const data = await api.getRooms();
      if (data.success) {
        setRooms(data.data?.rooms || (Array.isArray(data.data) ? data.data : []));
      }
    } catch (e) {
      console.warn('Rooms load error:', e.message);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await api.getUsers();
      if (data.success) {
        setUsers(data.data?.users || (Array.isArray(data.data) ? data.data : []));
      }
    } catch (e) {
      console.warn('Users load error:', e.message);
    }
  };

  const loadBookings = async () => {
    try {
      const data = await api.getBookings();
      if (data.success) {
        setBookings(data.data?.bookings || (Array.isArray(data.data) ? data.data : []));
      }
    } catch (e) {
      console.warn('Bookings load error:', e.message);
    }
  };

  const loadPayments = async () => {
    try {
      const data = await api.getPayments();
      if (data.success) {
        setPayments(data.data?.payments || (Array.isArray(data.data) ? data.data : []));
      }
    } catch (e) {
      console.warn('Payments load error:', e.message);
    }
  };

  const loadArtists = async () => {
    try {
      const data = await api.getPendingArtists();
      if (data.success) {
        setArtists(data.data?.artists || (Array.isArray(data.data) ? data.data : []));
      }
    } catch (e) {
      console.warn('Artists load error:', e.message);
    }
  };

  const loadTracks = async () => {
    try {
      const data = await api.getTracks();
      if (data.success) {
        const trackList = data.data?.tracks || (Array.isArray(data.data) ? data.data : []);
        setPublishedTracks(trackList);
      }
    } catch (e) {
      console.warn('Tracks load error:', e.message);
    }
  };

  const loadAllData = () => {
    if (!isAuth) return;
    loadDashboardData();
    loadCaptains();
    loadRooms();
    loadUsers();
    loadBookings();
    loadPayments();
    loadArtists();
    loadTracks();
  };

  useEffect(() => {
    if (isAuth) {
      loadAllData();
    }
  }, [isAuth, activeTab]);

  const showTemporarySuccess = (msg) => {
    setActionSuccessMsg(msg);
    setTimeout(() => setActionSuccessMsg(''), 4500);
  };

  // Auth Actions
  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setLoading(true);
    try {
      const res = await api.loginAdmin(email, password);
      if (res.success) {
        setIsAuth(true);
        loadAllData();
      }
    } catch (err) {
      setAuthError(err.message || 'Invalid admin credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    api.logout();
    setIsAuth(false);
  };

  // Drawer handlers
  const handleSelectDrawer = (item, type) => {
    setDrawerItem(item);
    setDrawerType(type);
    setRejectionReason('');
  };

  // Captain Actions
  const handleApproveCaptain = async (id) => {
    try {
      const res = await api.approveCaptain(id);
      if (res.success) {
        showTemporarySuccess('Captain approved successfully!');
        setDrawerItem(null);
        loadCaptains();
        loadDashboardData();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleRejectCaptain = async (captain) => {
    try {
      const capId = typeof captain === 'object' ? (captain._id || captain.id) : captain;
      const reason = (typeof captain === 'object' && captain.reason) ? captain.reason : (rejectionReason || 'Information rejected');
      const res = await api.rejectCaptain(capId, reason);
      if (res.success) {
        showTemporarySuccess('Captain rejected');
        setDrawerItem(null);
        loadCaptains();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleBlockCaptain = async (id) => {
    try {
      const res = await api.blockCaptain(id);
      if (res.success) {
        showTemporarySuccess('Captain account blocked');
        setDrawerItem(null);
        loadCaptains();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleUnblockCaptain = async (id) => {
    try {
      const res = await api.unblockCaptain(id);
      if (res.success) {
        showTemporarySuccess('Captain account unblocked');
        setDrawerItem(null);
        loadCaptains();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleToggleSubscription = async (id, currentStatus) => {
    try {
      const res = await api.toggleVendorSubscription(id, !currentStatus);
      if (res.success) {
        showTemporarySuccess(`Vendor subscription ${!currentStatus ? 'activated' : 'deactivated'}`);
        loadCaptains();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleUnlockMultipleModules = async (e) => {
    e.preventDefault();
    if (!unlockModalCaptain) return;
    if (unlockModules.length === 0) {
      alert('Please select at least one module license.');
      return;
    }
    try {
      const res = await api.unlockCaptainModules(
        unlockModalCaptain._id,
        unlockModules,
        parseInt(unlockDuration, 10) || 30
      );
      if (res.success) {
        showTemporarySuccess(`Unlocked modules for ${unlockModalCaptain.fullName || unlockModalCaptain.name}!`);
        setUnlockModalCaptain(null);
        setUnlockModules([]);
        loadCaptains();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleModuleCheckboxChange = (modName) => {
    if (unlockModules.includes(modName)) {
      setUnlockModules(unlockModules.filter((m) => m !== modName));
    } else {
      setUnlockModules([...unlockModules, modName]);
    }
  };

  // Artist Actions
  const handleApproveArtist = async (id) => {
    try {
      const res = await api.approveArtistProfile(id);
      if (res.success) {
        showTemporarySuccess('Artist profile verified & badge awarded!');
        loadArtists();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleRejectArtist = async (id) => {
    try {
      const res = await api.rejectArtistProfile(id, 'Verification details unconfirmed');
      if (res.success) {
        showTemporarySuccess('Artist profile application rejected');
        loadArtists();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  // Room Actions
  const handleApproveRoom = async (id) => {
    try {
      const res = await api.approveRoom(id);
      if (res.success) {
        showTemporarySuccess('Room listing approved!');
        setDrawerItem(null);
        loadRooms();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleRejectRoom = async (room) => {
    try {
      const roomId = typeof room === 'object' ? (room._id || room.id) : room;
      const reason = (typeof room === 'object' && (room.reason || room.rejectionReason)) ? (room.reason || room.rejectionReason) : (rejectionReason || 'Rejection by Admin');
      const res = await api.rejectRoom(roomId, reason);
      if (res.success) {
        showTemporarySuccess('Room listing rejected');
        setDrawerItem(null);
        loadRooms();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteRoom = async (id) => {
    if (!window.confirm('Delete this room listing permanently?')) return;
    try {
      const res = await api.deleteRoom(id);
      if (res.success) {
        showTemporarySuccess('Room deleted');
        setDrawerItem(null);
        loadRooms();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  // User Actions
  const handleBlockUser = async (id) => {
    try {
      const res = await api.blockUser(id);
      if (res.success) {
        showTemporarySuccess('User suspended');
        loadUsers();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleUnblockUser = async (id) => {
    try {
      const res = await api.unblockUser(id);
      if (res.success) {
        showTemporarySuccess('User access restored');
        loadUsers();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  // Room Slot Fetcher
  const handleFetchRoomSlots = async (roomId, dateStr) => {
    try {
      const res = await api.getRoomSlots(roomId, dateStr);
      if (res.success) {
        setSelectedSlots(res.data?.slots || res.data || []);
      }
    } catch (err) {
      console.warn('Failed to fetch room slots:', err.message);
    }
  };

  // Create Room / Warm Home Party Modal Handler
  const handleOpenCreateModal = (moduleType, categoryType) => {
    setCreateModuleType(moduleType);
    setCreateCategoryType(categoryType);
    setCreateTitle(categoryType === 'wormhole_party' ? 'VIP Warm Home Late-Night Party' : '');
    setCreateDesc(categoryType === 'wormhole_party' ? 'Exclusive late-night underground VIP party with live DJ, trap cyphers, & free drinks.' : '');
    setCreatePrice(categoryType === 'wormhole_party' ? '499' : '');
    setCreatePriceType(categoryType === 'wormhole_party' ? 'ticket' : (moduleType === 'building' ? 'month' : 'hour'));
    setCreateVenueAddress(categoryType === 'wormhole_party' ? 'Vijay Nagar Royal Lounge, Indore' : '');
    setCreateEventTime(categoryType === 'wormhole_party' ? '11:30 PM - 5:00 AM' : '');
    setCreateCity('Indore');
    setCreateImageUrl(categoryType === 'wormhole_party' ? 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=600&q=80' : '');
    setCreateCoupleAllowed(true);
    setShowCreateModal(true);
  };

  const handleCreateRoomSubmit = async (e) => {
    e.preventDefault();
    try {
      const eventTimeParts = (createEventTime || '11:00 PM - 05:00 AM').split('-');
      const startTime = eventTimeParts[0] ? eventTimeParts[0].trim() : '11:00 PM';
      const endTime = eventTimeParts[1] ? eventTimeParts[1].trim() : '05:00 AM';

      const payload = {
        title: createTitle.trim(),
        description: createDesc.trim(),
        price: parseFloat(createPrice) || 0,
        priceType: createPriceType,
        moduleType: createModuleType,
        categoryType: createCategoryType,
        city: createCity || 'Indore',
        address: createVenueAddress,
        eventTime: createEventTime,
        imageUrl: createImageUrl,
        isWormhole: createCategoryType === 'wormhole_party',
        isAdminManaged: true,
        coupleEntryAllowed: createCoupleAllowed,
        status: 'approved',
        moduleData: createModuleType === 'loka' ? {
          eventDate: new Date().toISOString().split('T')[0],
          startTime,
          endTime,
          entryType: (parseFloat(createPrice) || 0) > 0 ? 'paid' : 'free',
          ticketPrice: parseFloat(createPrice) || 0,
          totalCapacity: 100,
          availableTickets: 100,
          coupleEntryAllowed: createCoupleAllowed,
          isWormhole: createCategoryType === 'wormhole_party',
        } : (createModuleType === 'building' ? {
          propertyType: createCategoryType || 'flat',
          bhk: '2 BHK',
          monthlyRent: parseFloat(createPrice) || 0,
        } : {
          gymType: 'unisex',
          monthlyFee: parseFloat(createPrice) || 0,
        })
      };
      const res = await api.createRoom(payload);
      if (res.success) {
        showTemporarySuccess(
          createCategoryType === 'wormhole_party'
            ? '🎉 Warm Home Party published live by Admin!'
            : 'New listing published successfully!'
        );
        setShowCreateModal(false);
        loadRooms();
      }
    } catch (err) {
      alert(err.message || 'Failed to create room/party');
    }
  };

  if (!isAuth) {
    return (
      <LoginView
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        authError={authError}
        loading={loading}
        onLogin={handleLogin}
      />
    );
  }

  const safeTracks = Array.isArray(publishedTracks) ? publishedTracks : [];
  const safeArtists = Array.isArray(artists) ? artists : [];
  const pendingRoomsCount = rooms.filter((r) => r.status === 'pending').length;
  const pendingCaptainsCount = captains.filter((c) => c.status === 'pending').length;
  const pendingArtistsCount = safeArtists.filter((a) => !a.verified && a.status !== 'rejected').length;
  const approvalsCount = pendingRoomsCount + pendingCaptainsCount + pendingArtistsCount;

  return (
    <div className="admin-layout">
      {/* Top Glassmorphism Navigation Bar */}
      <HeaderNav
        theme={theme}
        setTheme={setTheme}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        cityFilter={cityFilter}
        setCityFilter={setCityFilter}
        onLogout={handleLogout}
        rooms={rooms}
        captains={captains}
        users={users}
        publishedTracks={safeTracks}
        setActiveTab={setActiveTab}
        onSelectDrawer={handleSelectDrawer}
        onOpenInstallPrompt={() => setShowInstallModal(true)}
      />

      {/* Main Container */}
      <div className="admin-main">
        {/* Sidebar Navigation */}
        <SidebarNav
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          artistsCount={safeArtists.filter((a) => !a.verified).length}
          tracksCount={safeTracks.length}
          approvalsCount={approvalsCount}
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
          onLogout={handleLogout}
          onOpenInstallPrompt={() => setShowInstallModal(true)}
        />

        {/* Dynamic Content Viewport */}
        <main className="content-viewport">
          {/* Action Notification Banner */}
          {actionSuccessMsg && (
            <div
              className="animate-fade"
              style={{
                marginBottom: '20px',
                padding: '14px 20px',
                borderRadius: 'var(--radius-md)',
                background: 'rgba(0, 255, 135, 0.15)',
                border: '1px solid rgba(0, 255, 135, 0.35)',
                color: 'var(--neon-green)',
                fontSize: '0.88rem',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                boxShadow: '0 0 20px rgba(0, 255, 135, 0.2)',
              }}
            >
              <CheckCircle2 size={18} />
              <span>{actionSuccessMsg}</span>
            </div>
          )}

          {/* Tab Views */}
          {activeTab === 'dashboard' && <DashboardView stats={stats} rooms={rooms} onSelectDrawer={handleSelectDrawer} />}
          {activeTab === 'approvals' && (
            <ApprovalsView
              captains={captains}
              artists={safeArtists}
              rooms={rooms}
              onApproveCaptain={handleApproveCaptain}
              onRejectCaptain={handleRejectCaptain}
              onApproveArtist={handleApproveArtist}
              onRejectArtist={handleRejectArtist}
              onApproveRoom={handleApproveRoom}
              onRejectRoom={handleRejectRoom}
              onSelectDrawer={handleSelectDrawer}
            />
          )}
          {activeTab === 'parties' && (
            <PartyManagerView
              rooms={rooms}
              onSelectDrawer={handleSelectDrawer}
              onOpenCreateModal={handleOpenCreateModal}
              onApproveRoom={handleApproveRoom}
              onRejectRoom={handleRejectRoom}
              onDeleteRoom={handleDeleteRoom}
            />
          )}
          {activeTab === 'bookings' && (
            <BookingsManagerView
              bookings={bookings}
              payments={payments}
              stats={stats}
              rooms={rooms}
              onCreateBooking={async (payload) => {
                try {
                  const res = await api.createAdminBooking(payload);
                  if (res.success) {
                    showTemporarySuccess('🎟️ Booking Pass issued and recorded in Escrow!');
                    loadBookings();
                    loadDashboardData();
                  }
                } catch (err) {
                  alert(err.message || 'Failed to issue booking pass');
                }
              }}
              onUpdateBookingStatus={async (id, bStatus, pStatus) => {
                try {
                  const res = await api.updateBookingStatus(id, bStatus, pStatus);
                  if (res.success) {
                    showTemporarySuccess(`Booking status updated to ${bStatus}!`);
                    loadBookings();
                  }
                } catch (err) {
                  alert(err.message || 'Failed to update booking status');
                }
              }}
            />
          )}
          {activeTab === 'music' && <MusicPublisher publishedTracks={safeTracks} loadTracks={loadTracks} showTemporarySuccess={showTemporarySuccess} />}
          {activeTab === 'loka' && <LokaMapView rooms={rooms} onSelectDrawer={handleSelectDrawer} onOpenCreateModal={handleOpenCreateModal} />}
          {activeTab === 'artists' && <ArtistsView artists={safeArtists} onApproveArtist={handleApproveArtist} onRejectArtist={handleRejectArtist} />}
          {activeTab === 'properties' && <PropertiesView rooms={rooms} onSelectDrawer={handleSelectDrawer} onOpenCreateModal={handleOpenCreateModal} showTemporarySuccess={showTemporarySuccess} />}
          {activeTab === 'fitness' && (
            <FitnessView
              rooms={rooms}
              selectedSlotRoom={selectedSlotRoom}
              setSelectedSlotRoom={setSelectedSlotRoom}
              selectedSlots={selectedSlots}
              setSelectedSlots={setSelectedSlots}
              selectedSlotDate={selectedSlotDate}
              setSelectedSlotDate={setSelectedSlotDate}
              onFetchRoomSlots={handleFetchRoomSlots}
              onSelectDrawer={handleSelectDrawer}
              onOpenCreateModal={handleOpenCreateModal}
            />
          )}
          {activeTab === 'captains' && (
            <CaptainsView
              captains={captains}
              onSelectDrawer={handleSelectDrawer}
              onApproveCaptain={handleApproveCaptain}
              onRejectCaptain={handleRejectCaptain}
              onBlockCaptain={handleBlockCaptain}
              onUnblockCaptain={handleUnblockCaptain}
              onToggleSubscription={handleToggleSubscription}
              onOpenUnlockModal={(cap) => {
                setUnlockModalCaptain(cap);
                setUnlockModules(cap.unlockedModules ? cap.unlockedModules.map((m) => (typeof m === 'string' ? m : m.moduleName)) : []);
              }}
              onLockModule={() => {}}
            />
          )}
          {activeTab === 'users' && <UsersView users={users} onBlockUser={handleBlockUser} onUnblockUser={handleUnblockUser} />}
          {activeTab === 'finance' && <FinanceView payments={payments} bookings={bookings} stats={stats} />}
          {activeTab === 'payments' && <PaymentsView showTemporarySuccess={showTemporarySuccess} />}
          {activeTab === 'ads' && <AdsManagerView showTemporarySuccess={showTemporarySuccess} />}
          {activeTab === 'settings' && <SettingsView theme={theme} setTheme={setTheme} onOpenInstallPrompt={() => setShowInstallModal(true)} />}
        </main>
      </div>

      {/* Slide-out Drawer Panel */}
      <DrawerModal
        drawerItem={drawerItem}
        drawerType={drawerType}
        onClose={() => setDrawerItem(null)}
        onApproveCaptain={handleApproveCaptain}
        onRejectCaptain={handleRejectCaptain}
        onBlockCaptain={handleBlockCaptain}
        onUnblockCaptain={handleUnblockCaptain}
        onOpenUnlockModal={(cap) => {
          setUnlockModalCaptain(cap);
          setUnlockModules(cap.unlockedModules ? cap.unlockedModules.map((m) => (typeof m === 'string' ? m : m.moduleName)) : []);
        }}
        onApproveRoom={handleApproveRoom}
        onRejectRoom={handleRejectRoom}
        onDeleteRoom={handleDeleteRoom}
        onResolveReport={() => {}}
      />

      {/* Unlock Multiple Modules Access License Modal */}
      <ModalContainer
        isOpen={!!unlockModalCaptain}
        onClose={() => setUnlockModalCaptain(null)}
        title={`Grant Access License: ${unlockModalCaptain?.fullName || unlockModalCaptain?.name || ''}`}
      >
        <form onSubmit={handleUnlockMultipleModules}>
          <div className="form-group">
            <label>Select Module Licenses to Unlock</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
              {[
                { id: 'fitness_sports', label: 'Fitness & Sports (Gyms & Ground Turfs)' },
                { id: 'building', label: 'Building Properties (Flats Rent & Sale)' },
                { id: 'loka', label: 'Rap Map (Hotzones, Underground Cyphers)' },
              ].map((mod) => (
                <label key={mod.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.88rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={unlockModules.includes(mod.id)}
                    onChange={() => handleModuleCheckboxChange(mod.id)}
                    style={{ width: '18px', height: '18px', accentColor: 'var(--neon-purple)' }}
                  />
                  <span>{mod.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group" style={{ marginTop: '20px' }}>
            <label>Access License Duration (Days)</label>
            <select className="form-control" value={unlockDuration} onChange={(e) => setUnlockDuration(e.target.value)}>
              <option value="30">30 Days (1 Month)</option>
              <option value="90">90 Days (3 Months)</option>
              <option value="365">365 Days (1 Year)</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button type="submit" className="btn btn-purple" style={{ flex: 1 }}>
              Save & Authorize Access
            </button>
            <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={() => setUnlockModalCaptain(null)}>
              Cancel
            </button>
          </div>
        </form>
      </ModalContainer>

      {/* Create Room / Warm Home Party Listing Modal */}
      <ModalContainer
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title={
          createCategoryType === 'wormhole_party'
            ? '🎉 Upload & Publish Warm Home Party (Admin Managed)'
            : `Add New ${createModuleType === 'building' ? 'Property' : createCategoryType === 'gym' ? 'Gym' : createCategoryType === 'ground_booking' ? 'Turf Ground' : 'Hotzone'} Listing`
        }
        maxWidth="620px"
      >
        <form onSubmit={handleCreateRoomSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {createCategoryType === 'wormhole_party' && (
            <div
              style={{
                padding: '12px 16px',
                borderRadius: '10px',
                background: 'rgba(139, 92, 246, 0.15)',
                border: '1px solid rgba(139, 92, 246, 0.4)',
                color: '#A78BFA',
                fontSize: '0.84rem',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <span>🌀 WORMHOLE PARTY MODE: Directly published live by Admin on RapMap hotzones & after-party radar!</span>
            </div>
          )}

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>{createCategoryType === 'wormhole_party' ? 'Party / Event Title *' : 'Listing Title *'}</label>
            <input
              type="text"
              className="form-control"
              placeholder={createCategoryType === 'wormhole_party' ? 'e.g. VIP Rooftop Warm Home Underground Party' : 'e.g. Vijay Nagar Royal Towers'}
              value={createTitle}
              onChange={(e) => setCreateTitle(e.target.value)}
              required
            />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Description & Vibe *</label>
            <textarea
              className="form-control"
              rows="3"
              placeholder="Provide event details, DJ lineup, entry rules..."
              value={createDesc}
              onChange={(e) => setCreateDesc(e.target.value)}
              required
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>City *</label>
              <select className="form-control" value={createCity} onChange={(e) => setCreateCity(e.target.value)}>
                <option value="Indore">Indore</option>
                <option value="Mumbai">Mumbai</option>
                <option value="Delhi">Delhi</option>
                <option value="Bangalore">Bangalore</option>
                <option value="Goa">Goa</option>
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Venue Address *</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. C21 VIP Lounge & Rooftop"
                value={createVenueAddress}
                onChange={(e) => setCreateVenueAddress(e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Price (INR) *</label>
              <input
                type="number"
                className="form-control"
                placeholder="499"
                value={createPrice}
                onChange={(e) => setCreatePrice(e.target.value)}
                required
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Timing / Schedule</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. 11:30 PM - 5:00 AM"
                value={createEventTime}
                onChange={(e) => setCreateEventTime(e.target.value)}
              />
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Poster Image URL</label>
            <input
              type="text"
              className="form-control"
              placeholder="https://images.unsplash.com/..."
              value={createImageUrl}
              onChange={(e) => setCreateImageUrl(e.target.value)}
            />
          </div>

          {createCategoryType === 'wormhole_party' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.88rem', cursor: 'pointer', marginTop: '4px' }}>
              <input
                type="checkbox"
                checked={createCoupleAllowed}
                onChange={(e) => setCreateCoupleAllowed(e.target.checked)}
                style={{ width: '18px', height: '18px', accentColor: 'var(--neon-purple)' }}
              />
              <span>Allow Couple Entry / VIP Passes</span>
            </label>
          )}

          <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
            <button type="submit" className="btn btn-purple" style={{ flex: 1 }}>
              {createCategoryType === 'wormhole_party' ? 'Publish Warm Home Party Live' : 'Publish Listing Live'}
            </button>
            <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={() => setShowCreateModal(false)}>
              Cancel
            </button>
          </div>
        </form>
      </ModalContainer>

      {/* PWA & Home Screen Shortcut Installation Popup & Floating Banner */}
      <PwaInstallPrompt
        isOpen={showInstallModal}
        onClose={() => setShowInstallModal(false)}
        triggerManual={() => setShowInstallModal(true)}
      />
    </div>
  );
}
